import rosterService from '../services/roster';
import { HIERARCHY_NODE_TYPE } from '../constants';

/*******************************************************************************
 * Initial state
 */
const initialState = {
    hierarchies: [],
    hierarchyNodeTypeList: [],
    errors: [],
    saving: false,
    loading: false
};

/*******************************************************************************
 * Non-async action types
 */
export const SET_HIERARCHIES = 'SET_HIERARCHIES';
export const SET_HIERARCHY_NODE_TYPE_LIST = 'SET_HIERARCHY_NODE_TYPE_LIST';
export const LOADING_START = 'LOADING_START';
export const LOADING_END = 'LOADING_END';
export const SAVING_START = 'SAVING_START';
export const SAVING_END = 'SAVING_END';
export const SET_ERRORS = 'SET_ERRORS';
export const CLEAR_ERRORS = 'CLEAR_ERRORS';

/*******************************************************************************
 * Reducer
 */
export default function appReducer( state = initialState, action ) {
    switch ( action.type ) {
        case SET_HIERARCHIES: {
            return {
                ...state,
                hierarchies: formatHierarchies( action.hierarchies )
            };
        }
        case SET_HIERARCHY_NODE_TYPE_LIST: {
            return {
                ...state,
                hierarchyNodeTypeList: action.hierarchyNodeTypeList.sort()
            };
        }
        case LOADING_START: {
            return {
                ...state,
                loading: true,
                error: []
            };
        }
        case LOADING_END: {
            return {
                ...state,
                loading: false
            };
        }
        case SAVING_START: {
            return {
                ...state,
                saving: true,
                error: []
            };
        }
        case SAVING_END: {
            return {
                ...state,
                saving: false
            };
        }
        case SET_ERRORS: {
            return {
                ...state,
                errors: [ ...state.errors, action.error ]
            };
        }
        case CLEAR_ERRORS: {
            return {
                ...state,
                errors: []
            };
        }
        default:
            return state;
    }
}

/*******************************************************************************
 * Action creators
 */

export function loadHierarchies( isUmbrella, companyId, accessToken, includeNodes = true ) {
        const RosterService = new rosterService( accessToken, companyId );
    return async ( dispatch, getState ) => {
        try {
            dispatch( startLoading() );
            if ( isUmbrella ) {
                getState().roster.hierarchy.errors.length && dispatch( clearErrors() );
            } else {
                getState().hierarchy.errors.length && dispatch( clearErrors() );
            }
            const hierarchies = await RosterService.listHierarchies();
            if ( includeNodes ) {
                await Promise.all( hierarchies.map( async hierarchy => {
                    hierarchy.hierarchyNodes = await RosterService.getHierarchyNodes( hierarchy.id );
                } ) );
            }
            dispatch( setHierarchies( hierarchies ) );
            return formatHierarchies( hierarchies );

        } catch ( error ) {
            dispatch( addError( error ) );
        } finally {
            dispatch( endLoading() );
        }
    };
}

export function loadHierarchyNodeTypes( accessToken, companyId, hierarchyId ) {
    const RosterService = new rosterService( accessToken, companyId );

    return async ( dispatch ) => {
        try {
            dispatch( startLoading() );

            const attributes = await RosterService.getHierarchyNodeAttributeTypeList();
            let types = [];

            const requestedAttribute = RosterService.findAttributeTypeByName( attributes, HIERARCHY_NODE_TYPE );
            if ( requestedAttribute ) {
                types = await RosterService.getHierarchyNodeAttributeValueList( hierarchyId, requestedAttribute.id );
            }

            dispatch( setHierarchyNodeTypeList( types ) );
            return types;

        } catch ( err ) {
            dispatch( addError( err ) );
            throw new Error( err );
        } finally {
            dispatch( endLoading() );
        }
    };
}

export function createHierarchy( accessToken, companyId, isUmbrella, name ) {
    const RosterService = new rosterService( accessToken, companyId );
    return async ( dispatch, getState ) => {
        try {
            const hierarchy = await RosterService.createHierarchy( name );
            const hierarchies = isUmbrella ? getState().roster.hierarchy.hierarchies : getState().hierarchy.hierarchies;
            dispatch( setHierarchies( [ ...hierarchies, hierarchy ] ) );
            return hierarchy;
        } catch ( error ) {
            throw error;
        }
    };
}

export function updateDefaultHierarchy( { isUmbrella, authCheck, companyId, id, name, primary = true } ) {
    const RosterService = new rosterService( authCheck, companyId );
    return async ( dispatch ) => {
        dispatch( startSaving() );
        try {
            const res = await RosterService.updatePrimaryHierarchy( {
                id,
                name,
                primary
            } );
            dispatch( endSaving() );
            dispatch( loadHierarchies( isUmbrella, companyId, authCheck ) );
            return res;

        } catch ( error ) {
            dispatch( addError( error ) );
        }
    };
}

export function startLoading() {
    return {
        type: LOADING_START
    };
}

export function endLoading() {
    return {
        type: LOADING_END
    };
}

export function startSaving() {
    return {
        type: SAVING_START
    };
}

export function endSaving() {
    return {
        type: SAVING_END
    };
}

export function setHierarchyNodeTypeList( hierarchyNodeTypeList ) {
    return {
        type: SET_HIERARCHY_NODE_TYPE_LIST,
        hierarchyNodeTypeList: hierarchyNodeTypeList
    };
}

export function setHierarchies( hierarchies ) {
    return {
        type: SET_HIERARCHIES,
        hierarchies
    };
}

export function addError( error ) {
    return {
        type: SET_ERRORS,
        error
    };
}

export function clearErrors() {
    return {
        type: CLEAR_ERRORS
    };
}


function formatHierarchies( hierarchies ) {
    return hierarchies.map( ( hierarchy ) => (
        {
            ...hierarchy,
            hierarchyNodes: formatHierarchyNodes( hierarchy.hierarchyNodes )
        }
    ) ).sort(
        ( a, b ) => a.name.localeCompare( b.name )
    );
}

function formatHierarchyNodes( nodes ) {
    if ( !nodes ) {
        return [];
    }
    // map all the nodes by id, and add children property
    const map = {};
    nodes.forEach( ( node ) => {
        map[ node.id ] = node;
        node.children = [];
    } );

    const rootNodes = [];
    nodes.sort(
        ( a, b ) => a.name.localeCompare( b.name )
    ).forEach( ( node ) => {
        if ( node.parentId ) {
            map[ node.parentId ].children.push( node );
        } else {
            rootNodes.push( node );
        }
    } );

    return rootNodes;
}
